import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiLogOut, FiUser, FiCalendar, FiFileText, FiUsers, FiDollarSign, FiShield, FiHome, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { auth, analytics, logEvent } from '../firebase';
import { signOut, onAuthStateChanged, User } from 'firebase/auth';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const LedenDashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loginAsUser, setLoginAsUser] = useState<any>(null);
  const [adminUser, setAdminUser] = useState<any>(null);
  const navigate = useNavigate();

  // Auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      
      if (!user) {
        // Als niet ingelogd, redirect naar login
        navigate('/leden-login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Google Analytics page view
  useEffect(() => {
    if (analytics && user) {
      logEvent(analytics, 'page_view', {
        page_title: 'Leden Dashboard',
        page_location: window.location.href
      });
    }
  }, [user]);

  // Check voor login as user
  useEffect(() => {
    const loginAsData = localStorage.getItem('loginAsUser');
    const adminData = localStorage.getItem('adminUser');
    
    if (loginAsData && adminData) {
      try {
        setLoginAsUser(JSON.parse(loginAsData));
        setAdminUser(JSON.parse(adminData));
      } catch (error) {
        console.error('Error parsing login as data:', error);
      }
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      
      // Google Analytics - logout
      if (analytics) {
        logEvent(analytics, 'logout');
      }
      
      navigate('/');
    } catch (err: any) {
      console.error('Logout error:', err);
    }
  };

  const handleBackToAdmin = () => {
    localStorage.removeItem('loginAsUser');
    localStorage.removeItem('adminUser');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Wordt doorgestuurd door useEffect
  }

  const dashboardItems = [
    {
      title: 'PRAC',
      description: 'Public Relations en Communicatie activiteiten',
      icon: FiShield,
      color: 'from-yellow-500 to-yellow-600',
      href: '/prac'
    },
    {
      title: 'Vergaderingen',
      description: 'Bekijk de notulen',
      icon: FiCalendar,
      color: 'from-blue-500 to-blue-600',
      href: '/vergaderingen'
    },
    {
      title: 'Ledenlijst',
      description: 'Contactgegevens van alle leden',
      icon: FiUsers,
      color: 'from-green-500 to-green-600',
      href: '/ledenlijst'
    },
    {
      title: 'Documenten',
      description: 'Interne documenten en formulieren',
      icon: FiFileText,
      color: 'from-purple-500 to-purple-600',
      href: '/documenten'
    },
    {
      title: 'Profiel',
      description: 'Beheer uw persoonlijke gegevens',
      icon: FiUser,
      color: 'from-indigo-500 to-indigo-600',
      href: '/profiel'
    }
  ];

  // Admin-specifieke items
  const adminItems = user?.email === 'p@de-boer.net' ? [
    {
      title: 'Leden Beheer',
      description: 'Beheer leden en rollen (Admin)',
      icon: FiUsers,
      color: 'from-red-500 to-red-600',
      href: '/leden-beheer'
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <FiShield className="text-xl text-indigo-900" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Leden Dashboard</h1>
                <p className="text-gray-300 text-sm">Lions Club Hoorn De Doelen</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right text-white">
                {loginAsUser ? (
                  <>
                    <p className="text-sm text-gray-300">Ingelogd als:</p>
                    <p className="font-medium">{loginAsUser.naam}</p>
                    <p className="text-xs text-yellow-400">({loginAsUser.email})</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-300">Welkom,</p>
                    <p className="font-medium">{user.email}</p>
                  </>
                )}
              </div>
              
              <div className="flex space-x-2">
                {loginAsUser && (
                  <button
                    onClick={handleBackToAdmin}
                    className="p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors"
                    title="Terug naar admin account"
                  >
                    <FiArrowLeft className="text-lg" />
                  </button>
                )}
                
                <button
                  onClick={() => navigate('/')}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  title="Terug naar hoofdpagina"
                >
                  <FiHome className="text-lg" />
                </button>
                
                <button
                  onClick={handleLogout}
                  className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <FiLogOut className="text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Welcome Section */}
          <motion.div variants={fadeIn} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Welkom in het Ledengedeelte
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Hier vindt u alle interne informatie en tools voor leden van Lions Club Hoorn De Doelen.
            </p>
          </motion.div>

          {/* Dashboard Grid */}
          <motion.div 
            variants={fadeIn}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
          >
            {dashboardItems.map((item, index) => (
              <motion.div
                key={item.title}
                variants={fadeIn}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:transform hover:-translate-y-1 cursor-pointer group"
                onClick={() => navigate(item.href)}
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${item.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
            
            {/* Admin Items */}
            {adminItems.map((item, index) => (
              <motion.div
                key={item.title}
                variants={fadeIn}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:transform hover:-translate-y-1 cursor-pointer group border-2 border-red-400/50"
                onClick={() => navigate(item.href)}
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${item.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>




        </motion.div>
      </main>
    </div>
  );
};

export default LedenDashboard; 